export interface QualityResult {
  score: number;
  isAcceptable: boolean;
  issues: string[];
  details: {
    brightness: number;
    contrast: number;
    sharpness: number;
    hasGlare: boolean;
  };
}

/**
 * Analyze image quality for ID verification
 * Checks for: Blur, Low Light, Glare, Low Contrast
 */
export async function analyzeImageQuality(file: File): Promise<QualityResult> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const objectUrl = URL.createObjectURL(file);

    img.onload = () => {
      URL.revokeObjectURL(objectUrl);
      
      try {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        
        if (!ctx) {
          reject(new Error('Could not get canvas context'));
          return;
        }

        // Resize for performance (max dimension 800px)
        const MAX_DIM = 800;
        let width = img.width;
        let height = img.height;
        
        if (width > MAX_DIM || height > MAX_DIM) {
          const ratio = Math.min(MAX_DIM / width, MAX_DIM / height);
          width = Math.round(width * ratio);
          height = Math.round(height * ratio);
        }

        canvas.width = width;
        canvas.height = height;
        ctx.drawImage(img, 0, 0, width, height);

        const imageData = ctx.getImageData(0, 0, width, height);
        const data = imageData.data;
        
        // Analysis variables
        let totalBrightness = 0;
        let totalSqBrightness = 0; // For variance/contrast
        let overexposedPixels = 0;
        
        // Convert to grayscale and calculate stats
        const grayData = new Uint8ClampedArray(width * height);
        
        for (let i = 0; i < data.length; i += 4) {
          // Weighted grayscale (rec601)
          const brightness = 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
          grayData[i / 4] = brightness;
          
          totalBrightness += brightness;
          totalSqBrightness += brightness * brightness;
          
          // Glare check: pixels near pure white
          if (brightness > 240) {
            overexposedPixels++;
          }
        }
        
        const pixelCount = width * height;
        const avgBrightness = totalBrightness / pixelCount;
        
        // Standard Deviation = sqrt(E[X^2] - (E[X])^2)
        const variance = (totalSqBrightness / pixelCount) - (avgBrightness * avgBrightness);
        const contrast = Math.sqrt(Math.max(0, variance)); // Std Dev
        
        // Sharpness (Laplacian variance)
        // Simply check difference between adjacent pixels
        let edgeSum = 0;
        
        for (let y = 1; y < height - 1; y++) {
          for (let x = 1; x < width - 1; x++) {
            const idx = y * width + x;
            const val = grayData[idx];
            
            // Simple Laplacian kernel:
            //  0  -1   0
            // -1   4  -1
            //  0  -1   0
            
            const laplacian = 
              4 * val
              - grayData[idx - 1]         // left
              - grayData[idx + 1]         // right
              - grayData[idx - width]     // top
              - grayData[idx + width];    // bottom
              
            edgeSum += Math.abs(laplacian);
          }
        }
        
        const avgEdge = edgeSum / pixelCount;
        
        // Thresholds
        const issues: string[] = [];
        let score = 100;
        
        // 1. Brightness Check (0-255)
        if (avgBrightness < 60) {
          issues.push('Too dark. Please move to a brighter area.');
          score -= 30;
        } else if (avgBrightness > 220) {
          issues.push('Too bright. Please reduce lighting.');
          score -= 20;
        }
        
        // 2. Contrast Check
        if (contrast < 30) {
          issues.push('Low contrast. Ensure ID is clearly visible against background.');
          score -= 20;
        }
        
        // 3. Glare Check
        const glareRatio = overexposedPixels / pixelCount;
        const hasGlare = glareRatio > 0.05; // >5% overexposed
        if (hasGlare) {
          issues.push('Glare detected. Please avoid direct light reflection.');
          score -= 25;
        }
        
        // 4. Sharpness/Blur Check
        // Heuristic: avgEdge < 5 usually means blurry
        // Text documents usually have high edge values
        const sharpness = avgEdge;
        if (sharpness < 7) {
            issues.push('Image is blurry. Please hold steady.');
            score -= 40;
        } else if (sharpness < 12) {
            // Mild blur warning but maybe acceptable
            score -= 10;
        }
        
        resolve({
          score: Math.max(0, score),
          isAcceptable: score >= 70, // Threshold for acceptance
          issues,
          details: {
            brightness: avgBrightness,
            contrast,
            sharpness,
            hasGlare
          }
        });
        
      } catch (err) {
        reject(err);
      }
    };

    img.onerror = () => {
      reject(new Error('Failed to load image for analysis'));
    };
    
    img.src = objectUrl;
  });
}
